import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../utils/api";
import { FiArrowLeft, FiMail, FiPhone, FiEye, FiEyeOff } from "react-icons/fi";

const ForgotPasswordFlow = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState("identify"); // identify, verify, reset
  const [identificationMethod, setIdentificationMethod] = useState("email"); // email or mobile
  const [emailOrMobile, setEmailOrMobile] = useState("");
  const [userType, setUserType] = useState("staff"); // staff, admin, vendor, customer
  const [otp, setOtp] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetType, setResetType] = useState(""); // email_link or otp
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const getLoginRedirectPath = () => {
    if (userType === "admin") {
      return "/restaurant/login";
    } else if (userType === "vendor") {
      return "/vendor/login";
    } else if (userType === "customer") {
      return "/customer";
    }
    return "/staff-login";
  };

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const email = params.get("email");
    const type = params.get("user_type") || params.get("type") || "staff";

    if (token && email) {
      setEmailOrMobile(email);
      setVerificationToken(token);
      setUserType(type);
      setCurrentStep("reset");
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 1: IDENTIFY USER
  // ═══════════════════════════════════════════════════════════════════════════

  const handleInitiateForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Validate input
      if (!emailOrMobile.trim()) {
        setError("Please enter your email or mobile number");
        setLoading(false);
        return;
      }

      // Detect if email or mobile
      const isEmail = emailOrMobile.includes("@");
      const isMobile = /^\d{10,}$/.test(emailOrMobile.replace(/[^\d]/g, ""));

      if (!isEmail && !isMobile) {
        setError("Please enter a valid email or mobile number");
        setLoading(false);
        return;
      }

      // Call forgot password endpoint
      const response = await api.post(
        "restaurant/initiate-forgot-password",
        {
          email_or_mobile: emailOrMobile,
          user_type: userType,
        },
        {},
        false
      );

      if (response.success || response.data) {
        setResetType(response.data?.resetType || "email_link");
        setSuccess(
          response.data?.message ||
            "Reset instructions sent to your account"
        );
        setCurrentStep("verify");
      } else {
        setError(response.message || "Failed to initiate password reset");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 2: VERIFY (OTP OR EMAIL LINK)
  // ═══════════════════════════════════════════════════════════════════════════

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!otp.trim()) {
        setError("Please enter the OTP");
        setLoading(false);
        return;
      }

      if (otp.length !== 6) {
        setError("OTP must be 6 digits");
        setLoading(false);
        return;
      }

      // Verify OTP
      const response = await api.post(
        "restaurant/verify-otp",
        {
          mobile_or_email: emailOrMobile,
          otp: otp,
        },
        {},
        false
      );

      if (response.success || response.data) {
        setVerificationToken(response.data?.otpId || otp);
        setSuccess("OTP verified successfully!");
        setCurrentStep("reset");
      } else {
        setError(response.message || "Invalid OTP");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailToken = async (token) => {
    setLoading(true);
    setError("");

    try {
      // Verify email token
      const response = await api.post(
        "restaurant/verify-reset-token",
        {
          token: token,
        },
        {},
        false
      );

      if (response.success || response.data) {
        setVerificationToken(token);
        setSuccess("Email verified successfully!");
        setCurrentStep("reset");
      } else {
        setError(response.message || "Invalid verification token");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("Failed to verify token");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP 3: RESET PASSWORD
  // ═══════════════════════════════════════════════════════════════════════════

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Validate passwords
      if (!newPassword.trim()) {
        setError("Please enter a new password");
        setLoading(false);
        return;
      }

      if (newPassword.length < 8) {
        setError("Password must be at least 8 characters long");
        setLoading(false);
        return;
      }

      if (newPassword !== confirmPassword) {
        setError("Passwords do not match");
        setLoading(false);
        return;
      }

      // Reset password
      const response = await api.post(
        "restaurant/reset-password-new",
        {
          email_or_mobile: emailOrMobile,
          new_password: newPassword,
          confirm_password: confirmPassword,
          user_type: userType,
          reset_token: verificationToken,
        },
        {},
        false
      );

      if (response.success || response.data) {
        setSuccess("Password reset successfully! Redirecting to login...");
        toast.success("Password reset successfully!");

        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate(getLoginRedirectPath());
        }, 2000);
      } else {
        setError(response.message || "Failed to reset password");
      }
    } catch (err) {
      console.error("Error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: STEP 1 - IDENTIFY USER
  // ═══════════════════════════════════════════════════════════════════════════

  if (currentStep === "identify") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <FiArrowLeft size={20} />
            Back
          </button>

          {/* Header */}
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Forgot Password?
          </h1>
          <p className="text-gray-600 text-sm mb-6">
            Enter your email or mobile number to reset your password
          </p>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          <form onSubmit={handleInitiateForgotPassword} className="space-y-4">
            {/* User Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Type
              </label>
              <select
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                <option value="vendor">Vendor</option>
                <option value="customer">Customer</option>
              </select>
            </div>

            {/* Email or Mobile Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email or Mobile Number
              </label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-400">
                  {emailOrMobile.includes("@") ? (
                    <FiMail size={20} />
                  ) : (
                    <FiPhone size={20} />
                  )}
                </span>
                <input
                  type="text"
                  value={emailOrMobile}
                  onChange={(e) => setEmailOrMobile(e.target.value)}
                  placeholder="example@email.com or 9876543210"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 rounded-lg transition"
            >
              {loading ? "Sending..." : "Send Reset Instructions"}
            </button>
          </form>

          {/* Login Link */}
          <p className="text-center text-gray-600 text-sm mt-6">
            Remember your password?{" "}
            <button
              onClick={() => navigate(getLoginRedirectPath())}
              className="text-blue-600 hover:text-blue-800 font-semibold"
            >
              Login
            </button>
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: STEP 2 - VERIFY (OTP)
  // ═══════════════════════════════════════════════════════════════════════════

  if (currentStep === "verify" && resetType === "otp") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          {/* Back Button */}
          <button
            onClick={() => setCurrentStep("identify")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <FiArrowLeft size={20} />
            Back
          </button>

          {/* Header */}
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Enter OTP
          </h1>
          <p className="text-gray-600 text-sm mb-6">
            Enter the 6-digit OTP sent to {emailOrMobile}
          </p>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleVerifyOTP} className="space-y-4">
            {/* OTP Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                OTP Code
              </label>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                className="w-full px-4 py-2 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-gray-500 text-xs mt-2">
                OTP expires in 10 minutes
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 rounded-lg transition"
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>
          </form>

          {/* Resend OTP Link */}
          <p className="text-center text-gray-600 text-sm mt-6">
            Didn't receive OTP?{" "}
            <button
              onClick={handleInitiateForgotPassword}
              className="text-blue-600 hover:text-blue-800 font-semibold"
            >
              Resend
            </button>
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: STEP 3 - RESET PASSWORD
  // ═══════════════════════════════════════════════════════════════════════════

  if (currentStep === "reset") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          {/* Back Button */}
          <button
            onClick={() => setCurrentStep("verify")}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <FiArrowLeft size={20} />
            Back
          </button>

          {/* Header */}
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Create New Password
          </h1>
          <p className="text-gray-600 text-sm mb-6">
            Enter a strong password to secure your account
          </p>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          <form onSubmit={handleResetPassword} className="space-y-4">
            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showNewPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            {/* Password Strength Indicator */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs font-medium text-gray-700 mb-2">
                Password Requirements:
              </p>
              <ul className="text-xs text-gray-600 space-y-1">
                <li
                  className={
                    newPassword.length >= 8
                      ? "text-green-600"
                      : "text-gray-600"
                  }
                >
                  ✓ At least 8 characters
                </li>
                <li
                  className={
                    newPassword !== confirmPassword || !confirmPassword
                      ? "text-gray-600"
                      : "text-green-600"
                  }
                >
                  ✓ Passwords match
                </li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 rounded-lg transition"
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
        </div>
      </div>
    );
  }
};

export default ForgotPasswordFlow;
